import type { ClientType, FoodCategory, Market } from '../types';

export const FCC_META_ANCHORS = [
  'Meta de 2026: 9% de resultado líquido com crescimento sustentável.',
  'Cada 1% de desconto compromete mais de 10% do lucro quando a margem líquida está em 9%.',
  'Quem entra sem preparo tende a sair com desconto; quem entra com método protege margem.'
];

export const FCC_METHOD_ANCHORS = [
  'Leitura de mercado antes da semana: cenário atual, concorrência, maior oportunidade e foco prioritário.',
  'Leitura de loja em 3 minutos antes da negociação no mercado interno.',
  'Pré-pedido em 3 níveis: Essencial, Recomendado e de maior valor.',
  'Retorno padrão em +1, +3 e +7 para proteger pedido, execução e próximo ciclo.',
  'Planejar a rota pelo 3M: Maior Potencial, Maior Mix e Maior Oportunidade.',
  'Entender antes de atender: chegue com dados, histórico e proposta prática.',
  'Concessão sem contrapartida não é negociação; é prejuízo disfarçado.'
];

export const FCC_TOOLS_BY_MARKET: Record<Market, string[]> = {
  interno: ['Power BI', 'CRM', 'Scanntech', 'Força de Vendas', 'PDN'],
  externo: ['Power BI', 'CRM', 'S&OP', 'forecast por cliente', 'calendário de embarque']
};

export const FCC_CHANNEL_PLAYBOOKS: Record<
  ClientType,
  {
    focus: string[];
    metrics: string[];
    pressurePoints: string[];
    counterpartyIdeas: string[];
    nextStep: string;
  }
> = {
  varejo_independente: {
    focus: ['mix básico mais item complementar', 'giro da categoria', 'relação de longo prazo'],
    metrics: ['giro', 'margem', 'ruptura', 'ticket médio'],
    pressurePoints: ['falta de espaço', 'decisão muito centrada em preço', 'baixa disciplina de exposição'],
    counterpartyIdeas: ['ponto extra por 30 dias', 'aumento de mix', 'reposição mínima acordada'],
    nextStep: 'sair com teste simples de mix e compromisso de exposição ou reposição.'
  },
  supermercado_bairro: {
    focus: ['mix ajustado ao bairro', 'execução de gôndola', 'combinação entre margem e conveniência'],
    metrics: ['saída da categoria', 'giro', 'ruptura', 'exposição'],
    pressurePoints: ['ruptura em datas fortes', 'mix pouco produtivo', 'precificação inconsistente'],
    counterpartyIdeas: ['ajuste de planograma', 'encarte local', 'degustação'],
    nextStep: 'validar um ajuste rápido de mix e exposição com medição de giro.'
  },
  rede_regional: {
    focus: ['dados de saída da categoria', 'planejamento conjunto', 'ampliação de mix com disciplina de margem'],
    metrics: ['saída da categoria', 'participação', 'margem em reais', 'rentabilidade da categoria'],
    pressurePoints: ['pedidos de desconto fora da banda', 'pressão do concorrente', 'gap de mix entre lojas'],
    counterpartyIdeas: ['encarte', 'ponto extra', 'meta de saída da categoria', 'inclusão de novos SKUs'],
    nextStep: 'amarrar plano conjunto por grupo de lojas ou loja-piloto com contrapartida formal.'
  },
  varejo_nacional: {
    focus: ['negociação estruturada', 'JBP', 'governança de preço e mix'],
    metrics: ['faturamento', 'margem de contribuição', 'participação', 'frequência de compra'],
    pressurePoints: ['alto poder de barganha', 'comparação direta com concorrência', 'demanda por investimentos'],
    counterpartyIdeas: ['vigência de preço', 'meta de volume', 'distribuição nacional', 'calendário promocional'],
    nextStep: 'formalizar plano de crescimento com critérios de volume, investimento e execução.'
  },
  atacado_cash_carry: {
    focus: ['volume com margem protegida', 'giro rápido', 'competitividade sem perder banda'],
    metrics: ['volume', 'giro', 'margem em reais', 'margem de preço'],
    pressurePoints: ['guerra de preço', 'pressão por volume', 'sensibilidade a ruptura'],
    counterpartyIdeas: ['volume mínimo', 'janela promocional controlada', 'mix de maior valor'],
    nextStep: 'fechar volume condicionado a mix e disciplina de preço.'
  },
  distribuidor: {
    focus: ['desenvolvimento de território', 'capilaridade', 'regularidade de abastecimento'],
    metrics: ['volume', 'faturamento', 'participação regional', 'cadência de compra'],
    pressurePoints: ['cobertura irregular', 'foco excessivo em preço', 'baixo desenvolvimento de mix'],
    counterpartyIdeas: ['meta de cobertura', 'campanha regional', 'plano de giro por SKU'],
    nextStep: 'alinhar PDN ou plano territorial com metas e contrapartidas claras.'
  },
  food_service: {
    focus: ['padronização', 'conveniência', 'frequência de compra'],
    metrics: ['rendimento', 'regularidade', 'ticket', 'mix por aplicação'],
    pressurePoints: ['sensibilidade a variação de padrão', 'demanda por praticidade', 'pressão de custo por uso'],
    counterpartyIdeas: ['teste operacional', 'mix por ocasião de consumo', 'calendário de reposição'],
    nextStep: 'avançar com teste de uso, rendimento e frequência ideal de reposição.'
  },
  key_account: {
    focus: ['estratégia conjunta', 'margem de contribuição', 'investimento trade com lógica de retorno'],
    metrics: ['margem de contribuição', 'crescimento de faturamento', 'prazo médio de pagamento', 'carteira ativa'],
    pressurePoints: ['alto nível de exigência', 'investimento trade', 'múltiplos decisores'],
    counterpartyIdeas: ['plano conjunto', 'volume incremental', 'mais espaço na gôndola', 'contrapartida de exposição'],
    nextStep: 'sair com plano executivo aprovado ou agenda formal para PDN.'
  },
  varejo_tradicional: {
    focus: ['frequência', 'mix básico', 'proximidade operacional'],
    metrics: ['giro', 'ruptura', 'faturamento', 'regularidade'],
    pressurePoints: ['baixo tempo para negociação', 'pouca sofisticação de dados', 'dependência de reposição'],
    counterpartyIdeas: ['reposição mínima', 'visita recorrente', 'introdução de item complementar'],
    nextStep: 'garantir reposição, mix essencial e compromisso de retorno rápido.'
  },
  industria: {
    focus: ['volume', 'regularidade', 'especificação técnica'],
    metrics: ['volume', 'regularidade', 'especificação', 'custo total'],
    pressurePoints: ['interrupção de fornecimento', 'exigência técnica', 'comparação fria de preço'],
    counterpartyIdeas: ['volume mínimo', 'vigência contratual', 'calendário de fornecimento'],
    nextStep: 'amarrar especificação, calendário e compromisso de volume.'
  },
  marca_propria: {
    focus: ['produção sob contrato', 'padronização', 'visão de longo prazo'],
    metrics: ['volume mínimo', 'regularidade', 'qualidade', 'vigência'],
    pressurePoints: ['pressão por custo', 'risco de desalinhamento de padrão', 'dependência de continuidade'],
    counterpartyIdeas: ['vigência contratual', 'volume mínimo', 'plano de fornecimento'],
    nextStep: 'fechar parâmetros de contrato e estabilidade de fornecimento.'
  },
  outro: {
    focus: ['leitura do negócio', 'valor percebido', 'resultado sustentável'],
    metrics: ['margem', 'giro', 'crescimento', 'previsibilidade'],
    pressurePoints: ['falta de critério', 'negociação reativa', 'baixa clareza de objetivos'],
    counterpartyIdeas: ['plano piloto', 'agenda de revisão', 'contrapartida operacional'],
    nextStep: 'tirar a reunião do genérico e amarrar um teste com objetivo definido.'
  },
  distribuidor_importador: {
    focus: ['nacionalização e distribuição local', 'previsibilidade de embarque', 'continuidade de fornecimento'],
    metrics: ['calendário de embarque', 'volume', 'conformidade', 'regularidade'],
    pressurePoints: ['pressão de preço FOB', 'troca de fornecedor', 'risco de ruptura na distribuição local'],
    counterpartyIdeas: ['volume mínimo', 'vigência de preço', 'calendário de 6 meses'],
    nextStep: 'fechar calendário, vigência e volume para reduzir risco comercial dos dois lados.'
  },
  food_service_internacional: {
    focus: ['padronização', 'segurança alimentar', 'consistência de entrega'],
    metrics: ['padronização', 'regularidade', 'rendimento', 'conformidade'],
    pressurePoints: ['sensibilidade a padrão', 'risco operacional', 'pressão por custo por uso'],
    counterpartyIdeas: ['teste operacional', 'programação de embarques', 'mix por aplicação'],
    nextStep: 'amarrar teste operacional ou calendário de fornecimento com critérios claros.'
  },
  varejo_internacional: {
    focus: ['conformidade', 'qualidade percebida', 'estabilidade de mix e calendário'],
    metrics: ['participação', 'regularidade', 'conformidade', 'giro'],
    pressurePoints: ['comparação entre marcas', 'sensibilidade a padrão', 'risco de imagem'],
    counterpartyIdeas: ['volume por janela', 'mix complementar', 'vigência de preço'],
    nextStep: 'construir proposta que una padrão, calendário e volume incremental.'
  },
  industria_processadora: {
    focus: ['continuidade operacional', 'especificação estável', 'custo de troca de fornecedor'],
    metrics: ['regularidade', 'especificação', 'custo total', 'volume'],
    pressurePoints: ['interrupção de planta', 'não conformidade', 'troca de fornecedor por preço'],
    counterpartyIdeas: ['contrato de volume', 'padrão técnico', 'calendário de entregas'],
    nextStep: 'avançar com compromisso técnico-operacional e vigência definida.'
  },
  trading: {
    focus: ['segurança de execução', 'previsibilidade documental', 'relação financeira de longo prazo'],
    metrics: ['câmbio', 'embarque', 'compliance documental', 'volume'],
    pressurePoints: ['pressão financeira de curto prazo', 'volatilidade', 'baixa fidelidade'],
    counterpartyIdeas: ['vigência', 'volume mínimo', 'calendário de embarque'],
    nextStep: 'tirar a conversa do spot puro e levar para compromisso de calendário e volume.'
  }
};

export const FCC_CATEGORY_PLAYBOOKS: Record<
  FoodCategory,
  {
    architecture: string;
    cvb: string[];
    proofPoints: string[];
    mixLogic: string[];
  }
> = {
  aves: {
    architecture: 'Alta representatividade, giro forte e referência de marca com espaço para cortes, temperados, empanados e especiais.',
    cvb: [
      'Temperado pronto para assar: conveniência real que aumenta giro, reduz perda e eleva ticket médio.',
      'Cortes padronizados: uniformidade de apresentação e maior confiança do consumidor na recompra.'
    ],
    proofPoints: ['qualidade certificada', 'padronização', 'conveniência', 'giro com margem'],
    mixLogic: ['item líder', 'complementar de conveniência', 'item de margem']
  },
  embutidos: {
    architecture: 'Categoria de valor agregado e conveniência com forte dependência de exposição e percepção de marca.',
    cvb: [
      'Produto líder regional: marca forte ajuda recompra e reduz risco de estoque parado.',
      'Linha completa por ocasião de consumo: melhora presença de gôndola e amplia margem da categoria.'
    ],
    proofPoints: ['marca forte', 'valor agregado', 'giro previsível', 'conveniência'],
    mixLogic: ['item líder', 'complementar', 'item de maior valor ou especialidade']
  },
  industrializados: {
    architecture: 'Linha com controle de qualidade rigoroso, apelo de conveniência e boa margem em reais.',
    cvb: [
      'Processo produtivo controlado: padrão sensorial superior e maior disposição do consumidor para pagar mais.',
      'Categoria de conveniência: melhora giro e margem quando entra com mix e exposição corretos.'
    ],
    proofPoints: ['padrão sensorial', 'conveniência', 'margem em reais', 'diferenciação'],
    mixLogic: ['item líder', 'complementar', 'item de maior valor']
  },
  pescados: {
    architecture: 'Categoria em crescimento acelerado, com forte argumento de rastreabilidade, origem e segurança alimentar.',
    cvb: [
      'Filé de tilápia rastreado e de produção própria: origem segura e categoria de maior valor com margem superior.',
      'Tecnologia IQF e padrão de textura: melhora percepção de qualidade e reduz risco operacional no cliente.'
    ],
    proofPoints: ['rastreabilidade', 'segurança alimentar', 'categoria de maior valor', 'qualidade percebida'],
    mixLogic: ['item líder', 'complementar', 'especialidade de margem']
  }
};

export const FCC_MPDV_ANCHORS = [
  'Cada SKU deve ter pelo menos uma frente e rótulo bem orientado.',
  'Produto bem exposto vende mais; execução é parte do papel comercial.',
  'Ruptura precisa ser sinalizada cedo para evitar perda de venda e desgaste com o cliente.',
  'Precificação e etiqueta próximas ao item facilitam compra e reforçam execução.',
  '70% das decisões de compra acontecem dentro do PDV.'
];
